import React, { useState, useEffect, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';

import Sidebar from './components/Sidebar';
import PDFViewer from './components/PDFViewer';
import QuestionForm from './components/QuestionForm';
import Splitter from './components/Splitter';
import InitialLoader from './components/InitialLoader';

import defaultConfig from './data/config.json';
import { questionnaireData as fallbackQuestionnaireData } from './data/config';
import { loadPapersForStudy, getResponseDocId } from './data/papersHelpers';
import {
  loadQuestionnaire,
  toLegacyShape,
  calculateQuestionnaireProgress,
} from './data/questionnaireHelpers';
import { loadAssignmentsForReviewer } from './data/assignmentsHelpers';
import { useActiveStudy } from './hooks/useActiveStudy';

function MainApp({ user }) {
  const { studyId, orgId, loading: studyLoading } = useActiveStudy();
  const [config, setConfig] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loadingPapers, setLoadingPapers] = useState(true);
  const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  // questionnaireRaw is the Firestore shape ({ sections: [...] }); legacy
  // is the transformed version QuestionForm consumes
  // ({ questionnaire: [{ section_title, ... }] }).
  const [questionnaireRaw, setQuestionnaireRaw] = useState(null);
  // Ticket 08 slice: assignments keyed by paperId for fast lookup.
  // Each entry is { questionIds: string[] } — the subset of leaf
  // questions this reviewer is expected to answer on that paper.
  const [assignmentsByPaperId, setAssignmentsByPaperId] = useState({});
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [splitRatio, setSplitRatio] = useState(50);
  const [responses, setResponses] = useState({});
  const [allUserResponses, setAllUserResponses] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    window.innerWidth < 1024
  );
  const [viewMode, setViewMode] = useState('split');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    // Ticket 11: configuration/main is denied by strict rules. The
    // fields MainApp used to read from there (users, assignedPapers,
    // papers) all moved to their own collections in earlier phase-1
    // tickets, so the config load is dead code. We keep defaultConfig
    // around as a compile-time shape hint for the researcher-papers
    // fallback branch below.
    setConfig(defaultConfig);
    setLoadingConfig(false);
  }, []);

  // Papers now come from the papers/{uuid} collection, not from config.papers.
  // Ticket 03's migration script seeds this collection; admin CRUD in
  // AdminConfigPage writes to it directly.
  useEffect(() => {
    if (!studyId) { setLoadingPapers(false); return; }
    let cancelled = false;
    setLoadingPapers(true);
    const run = async () => {
      try {
        const loaded = await loadPapersForStudy(studyId);
        if (!cancelled) setPapers(loaded);
      } catch (err) {
        console.error('Failed to load papers for study:', err);
        if (!cancelled) setPapers([]);
      } finally {
        if (!cancelled) setLoadingPapers(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  // Ticket 05: questionnaire also comes from Firestore now. Fall back to
  // the bundled module-level constant if the fetch fails so the reviewer
  // UI stays functional even when the network is bad.
  useEffect(() => {
    if (!studyId) { setLoadingQuestionnaire(false); return; }
    let cancelled = false;
    setLoadingQuestionnaire(true);
    const run = async () => {
      try {
        const loaded = await loadQuestionnaire(studyId);
        if (cancelled) return;
        setQuestionnaireRaw(loaded || null);
      } catch (err) {
        console.error('Failed to load questionnaire for study:', err);
        if (!cancelled) setQuestionnaireRaw(null);
      } finally {
        if (!cancelled) setLoadingQuestionnaire(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  // Ticket 08: load this reviewer's assignments for the active study. The
  // sidebar shows a paper only if there's a non-empty assignment for it,
  // and QuestionForm filters the rendered questions to the assignment's
  // questionIds.
  useEffect(() => {
    if (!user || !studyId) { setLoadingAssignments(false); return; }
    let cancelled = false;
    setLoadingAssignments(true);
    const run = async () => {
      try {
        const list = await loadAssignmentsForReviewer(studyId, user.uid);
        if (cancelled) return;
        const byId = {};
        list.forEach((a) => {
          byId[a.paperId] = a;
        });
        setAssignmentsByPaperId(byId);
      } catch (err) {
        console.error('Failed to load assignments for reviewer:', err);
        if (!cancelled) setAssignmentsByPaperId({});
      } finally {
        if (!cancelled) setLoadingAssignments(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [studyId, user]);

  useEffect(() => {
    if (!user || !studyId) {
      setAllUserResponses({});
      return;
    }
    let cancelled = false;
    const loadUserResponses = async () => {
      try {
        // Ticket 11 post-backfill: docs carry reviewerId; legacy docs
        // still use userId. Query by reviewerId first; the list rule is
        // permissive (signed-in only) so the query itself is fine, and
        // the client-side studyId filter narrows results to the active
        // study.
        const responsesQuery = query(
          collection(db, 'responses'),
          where('reviewerId', '==', user.uid),
          where('studyId', '==', studyId),
        );
        const querySnapshot = await getDocs(responsesQuery);
        const fetchedResponses = {};
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          fetchedResponses[data.paperId] = data;
        });
        if (!cancelled) setAllUserResponses(fetchedResponses);
      } catch (err) {
        console.error('Failed to load reviewer responses:', err);
        if (!cancelled) setAllUserResponses({});
      }
    };
    loadUserResponses();
    return () => {
      cancelled = true;
    };
  }, [user, studyId]);

  useEffect(() => {
    if (selectedPaper) {
      const paperData = allUserResponses[selectedPaper.id];
      // answers is the canonical field post-ticket-11; legacy docs
      // may still use `responses`. Read either.
      const paperResponses = paperData?.answers || paperData?.responses || {};
      const submittedStatus = paperData?.submitted || false;
      setResponses(paperResponses);
      setIsSubmitted(submittedStatus);
    }
  }, [selectedPaper, allUserResponses]);

  // Ticket 05: progress uses the loaded questionnaire + DSL visibility,
  // not the old hardcoded calculatePaperProgress. Falls back to the
  // module-level questionnaire if Firestore didn't return anything.
  const questionnaireForProgress = useMemo(() => {
    if (questionnaireRaw) return questionnaireRaw;
    // Build a minimal raw shape from the fallback module so the helper
    // can still compute progress.
    const legacySections = (fallbackQuestionnaireData.questionnaire || []).map(
      (s, i) => ({
        sectionId: `fb-${i + 1}`,
        title: s.section_title,
        description: s.section_description,
        questions: s.questions || [],
      }),
    );
    return { sections: legacySections };
  }, [questionnaireRaw]);

  const allPapersProgress = useMemo(() => {
    const progressMap = {};
    papers.forEach((paper) => {
      const currentPaperResponses =
        selectedPaper?.id === paper.id
          ? responses
          : allUserResponses[paper.id]?.answers ||
            allUserResponses[paper.id]?.responses ||
            {};
      progressMap[paper.id] = calculateQuestionnaireProgress(
        questionnaireForProgress,
        currentPaperResponses,
      );
    });
    return progressMap;
  }, [papers, selectedPaper, responses, allUserResponses, questionnaireForProgress]);

  const researcherPapers = useMemo(() => {
    if (loadingConfig || !user) return [];
    // Ticket 08: the assignments collection is now the source of truth.
    // A paper shows in the sidebar iff there's a non-empty assignment for
    // it. Fallback to config.users[email].assignedPapers is kept for the
    // moment the assignments load happens *after* the papers load — once
    // `assignmentsByPaperId` populates, that becomes the filter.
    const assignmentPaperIds = Object.keys(assignmentsByPaperId).filter(
      (pid) => (assignmentsByPaperId[pid].questionIds || []).length > 0,
    );
    if (assignmentPaperIds.length > 0) {
      const set = new Set(assignmentPaperIds);
      return papers.filter((p) => set.has(p.id));
    }
    // Fallback (pre-migration dev env, or empty assignments): use config
    // users' assignedPapers. Keeps the sidebar populated while the
    // assignments collection is being populated.
    const assigned = config?.users?.[user.email]?.assignedPapers || [];
    const assignedSet = new Set(assigned.map(String));
    return papers.filter(
      (p) =>
        assignedSet.has(String(p.id)) ||
        (p.legacyId !== undefined && assignedSet.has(String(p.legacyId))),
    );
  }, [loadingConfig, config, user, papers, assignmentsByPaperId]);

  // --- MODIFICATION START ---
  // By clearing the responses state immediately upon selection, we prevent the
  // QuestionForm from ever receiving stale data from the previously viewed paper.
  const handlePaperSelect = (paper) => {
    setResponses({});
    setSelectedPaper(paper);
  };

  // Auto-select the first paper when the list populates and nothing
  // is selected yet — so the reviewer never lands on a blank pane.
  useEffect(() => {
    if (!selectedPaper && researcherPapers.length > 0) {
      setSelectedPaper(researcherPapers[0]);
    }
  }, [selectedPaper, researcherPapers]);
  // --- MODIFICATION END ---

  const handleResponsesUpdate = async (newResponses) => {
    if (!selectedPaper || isSubmitted) return;
    if (!orgId || !studyId) return;
    setResponses(newResponses);

    const docId = getResponseDocId(user.uid, selectedPaper, studyId);
    const responseDocRef = doc(db, 'responses', docId);
    const paperTitle = selectedPaper.title;
    const { total, answered } = calculateQuestionnaireProgress(
      questionnaireForProgress,
      newResponses,
    );

    // Ticket 11 rules require orgId/studyId/reviewerId/paperId and an
    // `answers` map whose keys are a subset of the matching
    // assignment's questionIds. Stamp them on every write — this is
    // both a create and an update path because we setDoc with merge.
    await setDoc(
      responseDocRef,
      {
        orgId,
        studyId,
        reviewerId: user.uid,
        userId: user.uid,
        userEmail: user.email,
        paperId: selectedPaper.id,
        paperTitle,
        answers: newResponses,
        lastUpdated: serverTimestamp(),
        completed: answered === total && total > 0,
      },
      { merge: true }
    );

    setAllUserResponses((prev) => ({
      ...prev,
      [selectedPaper.id]: {
        ...prev[selectedPaper.id],
        orgId,
        studyId,
        reviewerId: user.uid,
        paperId: selectedPaper.id,
        answers: newResponses,
        completed: answered === total && total > 0,
      },
    }));
  };

  const handleFinalSubmit = async () => {
    if (!selectedPaper) return;
    if (!orgId || !studyId) return;

    const docId = getResponseDocId(user.uid, selectedPaper, studyId);
    const responseDocRef = doc(db, 'responses', docId);
    const { total, answered } = calculateQuestionnaireProgress(
      questionnaireForProgress,
      responses,
    );

    const dataToSubmit = {
      ...allUserResponses[selectedPaper.id],
      orgId,
      studyId,
      reviewerId: user.uid,
      paperId: selectedPaper.id,
      answers: responses,
      submitted: true,
      submittedAt: serverTimestamp(),
      completed: answered === total && total > 0,
    };
    // Defensive: strip any legacy `responses` key so rules-eval sees
    // only `answers`. Rules ignore extra fields but the pair can
    // confuse CSV export.
    delete dataToSubmit.responses;

    await setDoc(responseDocRef, dataToSubmit, { merge: true });

    setAllUserResponses((prev) => ({
      ...prev,
      [selectedPaper.id]: {
        ...prev[selectedPaper.id],
        ...dataToSubmit,
        submitted: true,
      },
    }));
    setIsSubmitted(true);
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      window.location.href = '/login';
    });
  };

  const dataReady = !loadingConfig && !studyLoading && !loadingPapers && !loadingQuestionnaire && !loadingAssignments;
  if (!dataReady) {
    return <InitialLoader />;
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex justify-between items-center academic-shadow flex-shrink-0">
        <h1 className="text-slate-900 text-xl lg:text-2xl font-bold">
          📚 Research Paper Analysis Platform
        </h1>
        <div className="flex items-center gap-4">
          {user && (
            <span className="text-sm text-slate-600">
              Welcome, {user.email}
            </span>
          )}
          <button
            onClick={handleLogout}
            className="font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <Sidebar
          papers={researcherPapers}
          selectedPaper={selectedPaper}
          onPaperSelect={handlePaperSelect}
          allResponses={allUserResponses}
          papersProgress={allPapersProgress}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {selectedPaper ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="bg-white px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 flex-shrink-0">
                <div className="text-slate-600 text-sm">
                  Currently reviewing:{' '}
                  <strong className="text-slate-900">
                    {selectedPaper.title}
                  </strong>
                </div>

                <div className="flex bg-slate-100 rounded-lg p-1 text-sm">
                  <button
                    onClick={() => setViewMode('split')}
                    className={`px-3 py-2 rounded-md transition-all duration-200 ${
                      viewMode === 'split'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Split View
                  </button>
                  <button
                    onClick={() => setViewMode('questions')}
                    className={`px-3 py-2 rounded-md transition-all duration-200 ${
                      viewMode === 'questions'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Questions Only
                  </button>
                  <button
                    onClick={() => setViewMode('pdf')}
                    className={`px-3 py-2 rounded-md transition-all duration-200 ${
                      viewMode === 'pdf'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    PDF Only
                  </button>
                </div>
              </div>

              <Splitter
                splitRatio={splitRatio}
                onSplitChange={setSplitRatio}
                viewMode={viewMode}
                leftContent={
                  <QuestionForm
                    paper={selectedPaper}
                    questionnaire={toLegacyShape(questionnaireRaw)}
                    assignedQuestionIds={
                      selectedPaper
                        ? assignmentsByPaperId[selectedPaper.id]?.questionIds
                        : undefined
                    }
                    responses={responses}
                    onResponsesUpdate={handleResponsesUpdate}
                    userId={user.uid}
                    isSubmitted={isSubmitted}
                    onSubmit={handleFinalSubmit}
                  />
                }
                rightContent={<PDFViewer paper={selectedPaper} />}
              />
            </div>
          ) : (
            <div className="p-4 lg:p-8 h-full flex items-center justify-center">
              <div className="text-center bg-white p-10 rounded-xl border border-slate-200 academic-shadow max-w-lg">
                {researcherPapers.length === 0 ? (
                  <>
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: '#E8F0F8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 28,
                        marginBottom: 16,
                        border: '1px solid #E2E0DA',
                      }}
                    >
                      📋
                    </div>
                    <h2 className="text-slate-900 mb-3 text-2xl font-bold">
                      No papers assigned yet
                    </h2>
                    <p className="text-slate-600 mb-2">
                      Your study admin hasn't assigned any papers to you yet.
                      Once they do, the papers will appear here automatically.
                    </p>
                    <p className="text-sm text-slate-500">
                      If you think this is a mistake, reach out to your
                      organization admin.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-slate-900 mb-3 text-2xl lg:text-3xl font-bold">
                      Your Workspace
                    </h2>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                      Select one of your assigned papers from the sidebar to
                      begin the review process.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MainApp;
